import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaHeart } from 'react-icons/fa';
import axios from 'axios';
import DesktopGallery from './DesktopGallery';
import MobileCameraRoll from './MobileCameraRoll';

const isDevelopment = process.env.NODE_ENV === 'development';
const axiosInstance = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : '',
  timeout: 30000,
  withCredentials: true // Important for cookies
});

function ProtectedGallery() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authData, setAuthData] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Determine if this is for mobile or desktop
  const isMobile = location.pathname.includes('mobile');
  const loginPath = isMobile ? '/camera-roll-mobile/login' : '/camera-roll/login';

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const response = await axiosInstance.get('/api/auth/gallery-verify');
        
        if (response.data.success) {
          setAuthData(response.data.data);
          setIsAuthenticated(true);
        } else {
          navigate(loginPath, { replace: true });
        }
      } catch (error) {
        console.log('Auth verification failed:', error.message);
        navigate(loginPath, { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, [navigate, loginPath]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-rose-50">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-lg shadow-pink-500/30 animate-pulse">
              <FaHeart className="text-white text-2xl" />
            </div>
            <FaSpinner className="absolute -bottom-1 -right-1 animate-spin text-2xl text-pink-500" />
          </div>
          <p className="text-gray-500 font-medium">{t('gallery.loading')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated - should have redirected, but just in case
  if (!isAuthenticated || !authData) {
    return null;
  }

  // Render the appropriate gallery component with hash from auth data
  if (isMobile) {
    return <MobileCameraRoll hashFromAuth={authData.hash} />;
  }
  
  return <DesktopGallery hashFromAuth={authData.hash} />;
}

export default ProtectedGallery;
